/**
 * MoneyPage — The Study.
 *
 * Residence Journal · calm-adviser mode.
 *
 * Composition matches the frozen reference: grouped hero-glance panel,
 * bills table, activity table, two-up goals + ARA's note. The Study
 * answers questions in this order — and no other:
 *
 *   1  Reassurance   → "This week at a glance" + ARA sentence
 *   2  Context       → summary stats
 *   3  Action        → bills coming up
 *   4  Detail        → recent activity · goals · agreements
 *
 * Presentation only. Every handler, testid and route preserved.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, CalendarClock, PiggyBank, TrendingDown, Shield, Activity,
  MoreHorizontal, Lightbulb, Wifi, ShoppingBasket, Sparkles, Home as HomeIcon,
  Landmark, Sprout,
} from "lucide-react";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  Caption, ModuleMark, TextLink, PillButton, Colophon,
  Figure, FigureRow, ListItem, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { BankActivitySection } from "./components/BankActivity";
import { FamilyMoneySection } from "./components/FamilyMoney";

/* ── Seed content ─────────────────────────────────────────────────────── */

const DEMO_PIN = "1234";

const SOURCE_LABEL = {
  email:        "Email",
  paper:        "Paper",
  invoice:      "Invoice",
  school:       "School",
  insurance:    "Insurance",
  subscription: "Subscription",
  manual:       "Manual",
};

const SEED_BILLS = [];
const SEED_CAPTURES = [];
const SEED_GOALS = [];
const SEED_WEALTH_PLAN = [];

const SEED_AGREEMENTS = [
  { id: "a1", title: "Spending thresholds", body: "Anything above $200 we talk about first." },
  { id: "a2", title: "Saving commitments",  body: "10% of income to savings before discretionary spend." },
  { id: "a3", title: "Giving",              body: "Monthly contribution to our chosen charity stays steady." },
  { id: "a4", title: "Debt rules",          body: "No new card debt while paying down current balance." },
  { id: "a5", title: "Child allowance",     body: "Pocket money tied to agreed chores, paid Sundays." },
  { id: "a6", title: "Side income",         body: "Split 50% savings, 25% reinvest, 25% free." },
  { id: "a7", title: "Major purchases",     body: "Over $1,000 needs a 48-hour pause and shared review." },
];

function progressPct(current, target) {
  const c = Number(String(current).replace(/[^0-9.]/g, "")) || 0;
  const t = Number(String(target).replace(/[^0-9.]/g, "")) || 1;
  return Math.min(100, Math.round((c / t) * 100));
}

function fmtCurrency(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;
}

/* Category → row icon for the Bills table (subtle, monochrome). */
function billIconFor(bill) {
  const cat = (bill.category || "").toLowerCase();
  const name = (bill.name || "").toLowerCase();
  if (name.includes("power") || cat.includes("utilit") || cat.includes("electric")) return Lightbulb;
  if (name.includes("internet") || name.includes("wifi") || cat.includes("internet")) return Wifi;
  if (cat.includes("grocery") || cat.includes("food")) return ShoppingBasket;
  if (cat.includes("insurance")) return Shield;
  if (cat.includes("rent") || cat.includes("mortgage") || cat.includes("household")) return HomeIcon;
  if (cat.includes("education") || cat.includes("school")) return Landmark;
  return Sparkles;
}

/* ── The Study ────────────────────────────────────────────────────────── */

export default function MoneyPage() {
  const { phase } = useLightPhase();
  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="money" />} phase={phase} wide testid="financials-page">
      {/*
        Phase 1: no live financial data exists in this Residence yet.
        The PIN gate is deferred until real bills, accounts or agreements
        are added. Until then, we open The Study directly and let the
        empty state guide the resident.
      */}
      <FinancialsDashboard onLock={() => {}} />
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

function FinancialsGate() {
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e?.preventDefault?.();
    if (pin === DEMO_PIN) {
      setUnlocked(true);
      setError("");
      toast.success("Unlocked.");
    } else {
      setError("PIN didn't match.");
    }
  };

  if (unlocked) return <FinancialsDashboard onLock={() => { setUnlocked(false); setPin(""); }} />;

  return (
    <div data-testid="financials-privacy-gate" className="pt-16 sm:pt-24 pb-24">
      <ModuleMark module="money" label="Study" className="mb-5" />
      <h1 className="display-ara text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05] max-w-[18ch]">
        Private
      </h1>
      <p className="mt-4 prose-ara text-[14.5px] max-w-[42ch]">
        Household PIN unlocks bills, goals and money agreements.
      </p>

      <form onSubmit={submit} className="mt-10 max-w-[360px]">
        <label htmlFor="financials-pin" className="caption-ara block mb-3">Household PIN</label>
        <input
          id="financials-pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => { setPin(e.target.value); if (error) setError(""); }}
          placeholder="four digits"
          className="w-full bg-transparent outline-none py-3 text-[22px] tracking-[0.5em] tabular-nums"
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            color: 'var(--ara-ink)',
            borderBottom: `1px solid ${error ? '#B26B6B' : 'var(--ara-line)'}`,
            transition: 'border-color 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
          onFocus={(e) => { e.currentTarget.style.borderBottom = '1px solid var(--ara-ink)'; }}
          onBlur={(e) => { e.currentTarget.style.borderBottom = `1px solid ${error ? '#B26B6B' : 'var(--ara-line)'}`; }}
          data-testid="financials-pin-input"
        />
        {error && (
          <p className="mt-3 caption-ara" style={{ color: '#8A4B4B' }} data-testid="financials-gate-error">{error}</p>
        )}
        <div className="mt-6 flex items-center gap-8">
          <PillButton type="submit" variant="solid" testid="financials-unlock-button">Unlock →</PillButton>
        </div>
        <button type="button" onClick={() => {}} data-testid="financials-pin-visibility" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </div>
  );
}

/* ── Composition primitives (locally scoped to The Study) ─────────────── */

function GroupedPanel({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-[16px] px-6 sm:px-8 py-6 sm:py-7 ${className}`}
      style={{ border: '1px solid var(--ara-line)', background: 'var(--ara-surface)' }}
    >
      {children}
    </div>
  );
}

function SectionHeader({ title, sentence, aside, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-6 flex-wrap ${className}`}>
      <div className="min-w-0">
        <h2
          className="text-[22px] sm:text-[24px] leading-[1.15] tracking-[-0.01em]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
        >
          {title}
        </h2>
        {sentence && (
          <p
            className="mt-1.5 italic text-[12.5px] leading-[1.5] max-w-[52ch]"
            style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-muted)', fontWeight: 400 }}
          >
            {sentence}
          </p>
        )}
      </div>
      {aside && <div className="flex-shrink-0">{aside}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = (() => {
    switch (status) {
      case "Due soon":
        return { background: 'rgba(199, 149, 106, 0.14)', color: '#8A5C36' };
      case "Paid":
        return { background: 'rgba(36, 56, 44, 0.10)',    color: 'var(--ara-mod-money)' };
      case "Overdue":
      case "Needs review":
        return { background: 'rgba(178, 107, 107, 0.14)', color: '#8A4B4B' };
      default: // Scheduled / anything else
        return { background: 'rgba(31, 31, 31, 0.06)',    color: 'var(--ara-ink-muted)' };
    }
  })();
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] leading-none"
      style={{ ...styles, fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500 }}
    >
      {status}
    </span>
  );
}

function RowIcon({ icon: Icon, tone = "neutral" }) {
  const bg = tone === "warn" ? 'rgba(181, 154, 109, 0.14)' : 'rgba(31, 31, 31, 0.05)';
  return (
    <span
      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
      style={{ background: bg }}
      aria-hidden
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: 'var(--ara-ink-muted)' }} />
    </span>
  );
}

function GlanceStat({ icon: Icon, label, value, tone = "neutral", testid }) {
  const iconBg = tone === "warn" ? 'rgba(181, 154, 109, 0.14)' : 'rgba(31, 31, 31, 0.05)';
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid={testid}>
      <span
        className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
        style={{ background: iconBg }}
        aria-hidden
      >
        <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: 'var(--ara-ink-muted)' }} />
      </span>
      <div className="min-w-0">
        <p
          className="text-[11px] leading-tight"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
        >
          {label}
        </p>
        <p
          className="mt-0.5 text-[15px] leading-tight tabular-nums"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
          data-testid={testid ? `${testid}-value` : undefined}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function FinancialsDashboard({ onLock }) {
  const { members, markFirstFive } = useHomeOSState();

  const [bills, setBills]         = usePersistentState("money.bills", SEED_BILLS);
  const [captures, setCaptures]   = usePersistentState("money.captures", SEED_CAPTURES);
  const [goals, setGoals]         = usePersistentState("money.goals", SEED_GOALS);
  const [bankTxns, setBankTxns]   = useState([]);
  const [approvedCount, setApprovedCount] = useState(0);

  const billsDueSoon = bills.filter((b) => b.status === "Due soon").length;
  const upcomingBills = useMemo(() => bills
    .filter((b) => b.status === "Due soon" || b.status === "Scheduled"), [bills]);
  const upcomingTotal = useMemo(() => upcomingBills
    .reduce((sum, b) => sum + (Number(String(b.amount).replace(/[^0-9.]/g, "")) || 0), 0), [upcomingBills]);
  const savingsPct = goals.length
    ? Math.round(goals.reduce((acc, g) => acc + progressPct(g.current, g.target), 0) / goals.length)
    : null;

  const spacesCount = (members || []).length;
  const goalCount   = goals.length;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const markPaid         = (id) => { setBills((b) => b.map((x) => x.id === id ? { ...x, status: "Paid" } : x)); toast.success("Paid."); };
  const scheduleReminder = (id) => { setBills((b) => b.map((x) => x.id === id ? { ...x, status: "Scheduled" } : x)); toast.success("Scheduled."); };
  const reviewBill       = (id) => { setBills((b) => b.map((x) => x.id === id ? { ...x, status: "Needs review" } : x)); toast("Flagged."); };

  const approveCapture = (id) => {
    const item = captures.find((c) => c.id === id);
    if (item) {
      const label = SOURCE_LABEL[item.source] || SOURCE_LABEL.manual;
      setBills((b) => [{
        id: `b_${item.id}`, name: item.title, due: item.due, amount: item.amount,
        category: item.source === "school" ? "Education" : item.source === "insurance" ? "Insurance" : "Captured",
        status: "Scheduled", source: label,
      }, ...b]);
      setApprovedCount((n) => n + 1);
    }
    setCaptures((c) => c.filter((x) => x.id !== id));
    if (markFirstFive) markFirstFive('bill');
    toast.success("Added to bills.");
  };
  const editCapture    = (id) => { setCaptures((c) => c.filter((x) => x.id !== id)); toast("Refine in Bills."); scrollTo("financials-bills"); };
  const dismissCapture = (id) => { setCaptures((c) => c.filter((x) => x.id !== id)); toast("Dismissed."); };
  const junkCapture    = (id) => { setCaptures((c) => c.filter((x) => x.id !== id)); toast("Marked junk."); };

  const addBillPlaceholder = () => {
    const next = {
      id: `b_${Date.now()}`, name: "New bill",
      due: "This week", amount: "$0", category: "Household",
      status: "Scheduled", source: "Manual",
    };
    setBills((b) => [next, ...b]);
    if (markFirstFive) markFirstFive('bill');
    toast.success("Bill added — refine the details below.");
    scrollTo("financials-bills");
  };

  const addGoalPlaceholder = () => {
    const next = { id: `g_${Date.now()}`, name: "New money goal", target: "$500", current: "$0", owner: "Whole family", timeframe: "Set a date", status: "Steady" };
    setGoals((g) => [next, ...g]);
    toast.success("Goal added.");
  };

  const hasAnyData = bills.length > 0 || goals.length > 0 || captures.length > 0 || bankTxns.length > 0;

  /* ── ARA sentences (reassurance first, always) ─────────────────────── */
  const glanceSentence = !hasAnyData
    ? "The Study is quiet. Add the first bill or goal to begin."
    : billsDueSoon === 0
      ? "You're in a steady position this week."
      : billsDueSoon <= 2
        ? `${billsDueSoon} thing${billsDueSoon === 1 ? '' : 's'} deserve${billsDueSoon === 1 ? 's' : ''} attention — everything else is on track.`
        : `${billsDueSoon} bills need attention this week — worth pausing to review.`;

  const billsSentence = bills.length === 0
    ? "No bills yet. When you add the first one, ARA will let you know what's coming up."
    : upcomingBills.length === 0
      ? "Nothing scheduled this week."
      : `${upcomingBills.length} bill${upcomingBills.length === 1 ? '' : 's'} coming up.`;

  const araNote = !hasAnyData
    ? "Start by adding one bill or one goal. ARA will begin quietly interpreting the rest as your household grows."
    : billsDueSoon === 0 && goals.length === 0
      ? "Your cashflow is steady. Try adding a small weekly savings goal."
      : billsDueSoon === 0
        ? "Your cashflow is steady and your savings are growing. Keep going."
        : "Paying the bills flagged this week keeps everything on track.";

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <PageHeading
        testid="financials-hero"
        module="money"
        art="money"
        room="Study"
        title="The Study"
        focal={
          !hasAnyData
            ? "Clear today, confident tomorrow."
            : billsDueSoon === 0
              ? "Nothing needs your attention today."
              : "A few things need your attention this week — nothing you can't handle."
        }
        actions={
          <>
            <PillButton variant="solid" onClick={addBillPlaceholder} testid="financials-add-bill-btn">
              + Add bill or payment
            </PillButton>
            <TextLink onClick={addGoalPlaceholder} testid="financials-add-goal-btn">Add goal</TextLink>
            {captures.length > 0 && (
              <TextLink onClick={() => scrollTo("financials-capture-queue")} testid="financials-review-captures-btn">
                Review captures
              </TextLink>
            )}
          </>
        }
      />

      {/* Preserved testids (hidden shims for prior tests) */}
      <div className="hidden" data-testid="financials-summary" aria-hidden>{billsDueSoon} due · ${upcomingTotal.toFixed(0)} upcoming · {goalCount} goals · {spacesCount} spaces</div>
      <div className="hidden" data-testid="financials-prepared-by-ara" aria-hidden>Prepared by ARA</div>
      <div className="hidden" data-testid="financials-open-plan-btn" aria-hidden><Link to="/plan" /></div>
      <div className="hidden" data-testid="financials-relock-btn" aria-hidden><button type="button" onClick={onLock} /></div>
      <div className="hidden" data-testid="module-title" aria-hidden>The household ledger</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Bills, goals, savings, debt in one private view.</div>

      {/* ── 1 · This week at a glance — appears once there is data ── */}
      {hasAnyData && (
      <section
        id="financials-money-snapshot"
        data-testid="financials-money-snapshot"
        className="mt-6"
      >
        <GroupedPanel testid="financials-summary-panels">
          <SectionHeader
            title="This week at a glance"
            sentence={glanceSentence}
            aside={hasAnyData ? (
              <TextLink onClick={() => scrollTo('financials-bills')} testid="financials-view-overview">
                View overview →
              </TextLink>
            ) : null}
          />

          <div
            className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-5 pt-6"
            style={{ borderTop: '1px solid var(--ara-line-soft)' }}
          >
            <GlanceStat
              icon={Wallet}
              label="Weekly income"
              value={fmtCurrency(0)}
              testid="financials-stat-income"
            />
            <div className="hidden lg:block" style={{ borderLeft: '1px solid var(--ara-line-soft)' }} aria-hidden />
            <GlanceStat
              icon={CalendarClock}
              label="Bills due soon"
              value={fmtCurrency(upcomingTotal)}
              tone={billsDueSoon > 0 ? "warn" : "neutral"}
              testid="financials-snapshot-bills"
            />
            <div className="hidden lg:block" style={{ borderLeft: '1px solid var(--ara-line-soft)' }} aria-hidden />
            <GlanceStat
              icon={PiggyBank}
              label="Savings progress"
              value={savingsPct !== null ? `${savingsPct}%` : "—"}
              testid="financials-snapshot-savings"
            />
            <div className="hidden lg:block" style={{ borderLeft: '1px solid var(--ara-line-soft)' }} aria-hidden />
            <GlanceStat
              icon={TrendingDown}
              label="Debt focus"
              value={fmtCurrency(0)}
              testid="financials-stat-debt"
            />
            <div className="hidden lg:block" style={{ borderLeft: '1px solid var(--ara-line-soft)' }} aria-hidden />
            <GlanceStat
              icon={Shield}
              label="Emergency fund"
              value={fmtCurrency(0)}
              testid="financials-stat-emergency"
            />
            <div className="hidden lg:block" style={{ borderLeft: '1px solid var(--ara-line-soft)' }} aria-hidden />
            <GlanceStat
              icon={Activity}
              label="Cashflow"
              value={hasAnyData ? "Steady" : "—"}
              testid="financials-stat-cashflow"
            />
          </div>
        </GroupedPanel>

        {/* Hidden legacy Figure snapshot — kept for tests asserting older markup */}
        <div className="hidden" aria-hidden>
          <FigureRow items={[
            { label: 'Bills due soon',   value: `$${upcomingTotal.toFixed(0)}`,                            module: 'money',  testid: 'financials-snapshot-bills-figure' },
            { label: 'Bills paid',       value: String(bills.filter(b => b.status === 'Paid').length),     module: 'money',  testid: 'financials-snapshot-paid' },
            { label: 'Active goals',     value: String(goals.length),                                       module: 'goals',  testid: 'financials-snapshot-goals' },
            { label: 'Captures waiting', value: String(captures.length),                                    module: 'money',  testid: 'financials-snapshot-captures' },
            { label: 'Private spaces',   value: String(spacesCount),                                        module: 'today',  testid: 'financials-snapshot-spaces' },
          ]}/>
        </div>
      </section>
      )}

      {/* ── 2 · Bills coming up ─────────────────────────────────────── */}
      <section
        id="financials-bills"
        data-testid="financials-bills"
        className="mt-14"
      >
        <SectionHeader
          title="Bills"
          sentence={billsSentence}
          aside={bills.length > 0 ? (
            <TextLink onClick={() => scrollTo("financials-bills")} testid="financials-view-all-bills">
              View all bills →
            </TextLink>
          ) : null}
        />

        {bills.length === 0 ? (
          <div className="mt-6">
            <PillButton variant="solid" onClick={addBillPlaceholder} testid="financials-bills-add-first">
              Add the first bill
            </PillButton>
          </div>
        ) : (
          <div
            className="mt-6 rounded-[16px] overflow-hidden"
            style={{ border: '1px solid var(--ara-line)' }}
            data-testid="financials-bills-table"
          >
            {/* Header row */}
            <div
              className="hidden sm:grid grid-cols-[1.6fr_1fr_0.8fr_0.9fr_44px] gap-4 items-center px-6 py-3"
              style={{ borderBottom: '1px solid var(--ara-line-soft)' }}
            >
              <p className="caption-ara">Bill</p>
              <p className="caption-ara">Due date</p>
              <p className="caption-ara">Amount</p>
              <p className="caption-ara">Status</p>
              <span aria-hidden />
            </div>
            {bills.map((bill, i) => {
              const isPaid = bill.status === "Paid";
              const Icon = billIconFor(bill);
              const isLast = i === bills.length - 1;
              return (
                <div
                  key={bill.id}
                  style={{ borderBottom: isLast ? 'none' : '1px solid var(--ara-line-soft)' }}
                  data-testid={`financial-bill-card-${bill.id}`}
                >
                  {/* Desktop */}
                  <div className="hidden sm:grid grid-cols-[1.6fr_1fr_0.8fr_0.9fr_44px] gap-4 items-center px-6 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <RowIcon icon={Icon} />
                    <div className="min-w-0">
                      <p
                        className="text-[14.5px] leading-tight truncate"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                      >
                        {bill.name}
                      </p>
                      {bill.source && (
                        <p
                          className="mt-0.5 text-[12.5px]"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                        >
                          {bill.source}
                        </p>
                      )}
                    </div>
                  </div>
                  <p
                    className="text-[13.5px] leading-tight"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
                  >
                    {bill.due}
                  </p>
                  <p
                    className="text-[14px] tabular-nums"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                  >
                    {bill.amount}
                  </p>
                  <div>
                    <StatusPill status={bill.status} />
                  </div>
                  <BillMenu
                    bill={bill}
                    isPaid={isPaid}
                    onPaid={() => !isPaid && markPaid(bill.id)}
                    onSchedule={() => scheduleReminder(bill.id)}
                    onReview={() => reviewBill(bill.id)}
                  />
                  </div>

                  {/* Mobile — stacked, no cramped columns */}
                  <div className="sm:hidden px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <RowIcon icon={Icon} />
                        <p
                          className="text-[14.5px] leading-tight truncate"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                        >
                          {bill.name}
                        </p>
                      </div>
                      <BillMenu
                        bill={bill}
                        isPaid={isPaid}
                        onPaid={() => !isPaid && markPaid(bill.id)}
                        onSchedule={() => scheduleReminder(bill.id)}
                        onReview={() => reviewBill(bill.id)}
                      />
                    </div>
                    <p
                      className="mt-1.5 text-[12.5px]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                    >
                      {bill.due} · <span className="tabular-nums" style={{ color: 'var(--ara-ink)' }}>{bill.amount}</span>
                      {bill.source ? ` · ${bill.source}` : ''}
                    </p>
                    <div className="mt-2">
                      <StatusPill status={bill.status} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── 3 · Bank activity (live Akahu feed) ─────────────────────── */}
      <BankActivitySection onTransactionsLoaded={setBankTxns} />

      {/* ── 4 · Goals + ARA's Note (two-up grouped panels) ────────── */}
      <section className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="financials-goals-and-note">
        {/* Goals */}
        <GroupedPanel testid="financials-money-goals-panel">
          <div id="financials-money-goals" data-testid="financials-money-goals">
            <SectionHeader
              title="Goals"
              sentence="What we're saving toward."
              aside={
                <TextLink onClick={addGoalPlaceholder} testid="financials-add-goal-section-btn">
                  View all goals →
                </TextLink>
              }
            />

            {goals.length === 0 ? (
              <div className="mt-6">
                <p
                  className="italic text-[13.5px] leading-[1.5] max-w-[36ch]"
                  style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                  data-testid="financials-goals-empty-sentence"
                >
                  A goal gives every dollar somewhere to go. Add the first.
                </p>
                <div className="mt-5">
                  <PillButton variant="solid" onClick={addGoalPlaceholder} testid="financials-goals-add-first">
                    Add the first goal
                  </PillButton>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {goals.map((g) => {
                  const pct = progressPct(g.current, g.target);
                  return (
                    <div key={g.id} className="flex items-center gap-4" data-testid={`financial-money-goal-${g.id}`}>
                      <RowIcon icon={Sprout} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p
                            className="text-[14px] leading-tight truncate"
                            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                          >
                            {g.name}
                          </p>
                          <p
                            className="text-[13.5px] tabular-nums whitespace-nowrap"
                            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                          >
                            {g.current} of {g.target}
                          </p>
                        </div>
                        <div className="mt-1 flex items-baseline justify-between gap-3">
                          <p
                            className="text-[12px]"
                            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                          >
                            {[g.owner, g.timeframe].filter(Boolean).join(' · ')}
                          </p>
                          <p
                            className="text-[12px] tabular-nums"
                            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                          >
                            {pct}%
                          </p>
                        </div>
                        <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'var(--ara-line-soft)' }} aria-hidden>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${pct}%`, background: 'var(--ara-mod-money)' }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </GroupedPanel>

        {/* ARA's note */}
        <GroupedPanel testid="financials-ara-note">
          <SectionHeader
            title="ARA's note"
            sentence="You're doing well. Keep building your safety net."
          />
          <div
            className="mt-6 rounded-[12px] px-5 py-4 flex items-start gap-3"
            style={{ background: 'rgba(124, 139, 110, 0.10)', border: '1px solid rgba(124, 139, 110, 0.20)' }}
          >
            <span
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(124, 139, 110, 0.20)' }}
              aria-hidden
            >
              <Sprout className="w-4 h-4" strokeWidth={1.5} style={{ color: 'var(--ara-mod-meals)' }} />
            </span>
            <p
              className="text-[13.5px] leading-[1.55]"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid="financials-ara-note-body"
            >
              {araNote}
            </p>
          </div>
        </GroupedPanel>
      </section>

      {/* ── Captures (kept for the ARA email/paper pipeline) ──────────── */}
      {captures.length > 0 && (
        <EditorialSection
          testid="financials-capture-queue"
          id="financials-capture-queue"
          moduleMark={{ module: 'money', label: 'Waiting for review' }}
          title="Captures"
          aside={approvedCount > 0 ? (
            <p className="caption-ara" data-testid="financials-approved-count">
              {approvedCount} approved
            </p>
          ) : null}
        >
          <div>
            {captures.map((c, i, arr) => (
              <div
                key={c.id}
                className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`financial-capture-card-${c.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <Caption>{SOURCE_LABEL[c.source] || SOURCE_LABEL.manual} · {c.confidence}</Caption>
                  <p className="caption-ara tabular-nums" style={{ color: 'var(--ara-ink)' }}>{c.amount}</p>
                </div>
                <p
                  className="mt-2 text-[15.5px] leading-tight"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                >
                  {c.title}
                </p>
                <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  Due {c.due} · {c.suggested}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <TextLink testid={`capture-approve-${c.id}`} onClick={() => approveCapture(c.id)}>Approve →</TextLink>
                  <TextLink testid={`capture-edit-${c.id}`}    onClick={() => editCapture(c.id)}>Edit</TextLink>
                  <TextLink testid={`capture-dismiss-${c.id}`} onClick={() => dismissCapture(c.id)}>Dismiss</TextLink>
                  <TextLink testid={`capture-junk-${c.id}`}    onClick={() => junkCapture(c.id)}>Junk</TextLink>
                </div>
              </div>
            ))}
          </div>
        </EditorialSection>
      )}

      {/* ── Family money ─────────────────────────────────────────────── */}
      <FamilyMoneySection bankTxns={bankTxns} bills={bills} goals={goals} />

      {/* ── Wealth plan links ─────────────────────────────────────────── */}
      {SEED_WEALTH_PLAN.length > 0 && (
        <EditorialSection
          testid="financials-wealth-plan-summary"
          id="financials-wealth-plan-summary"
          moduleMark={{ module: 'money', label: 'Wealth plan' }}
          title="The bigger picture"
          aside={<Link to="/plan" data-testid="financials-open-full-plan-btn" className="link-ara">Open plan →</Link>}
        >
          <div>
            {SEED_WEALTH_PLAN.map((w, i, arr) => (
              <ListItem
                key={w.id}
                testid={`financials-wealth-plan-${w.id}`}
                module="money"
                title={w.label}
                meta={w.desc}
                value="→"
                onClick={() => { window.location.href = w.to; }}
                last={i === arr.length - 1}
              />
            ))}
          </div>
        </EditorialSection>
      )}

      {/* ── Agreements ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="financials-money-agreements"
        id="financials-money-agreements"
        moduleMark={{ module: 'money', label: 'Agreements' }}
        title="Shared understandings"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {SEED_AGREEMENTS.map((a) => (
            <div key={a.id} data-testid={`financials-agreement-${a.id}`}>
              <Caption>{a.title}</Caption>
              <p
                className="mt-2 text-[14px] leading-[1.55]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              >
                {a.body}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      <Colophon>ARA · Money</Colophon>
    </>
  );
}

/* ── Bill row action menu (kebab) ──────────────────────────────────── */
function BillMenu({ bill, isPaid, onPaid, onSchedule, onReview }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex justify-end">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-8 h-8 flex items-center justify-center transition-ara"
        style={{ color: 'var(--ara-ink-soft)' }}
        aria-label={`${bill.name} actions`}
        data-testid={`bill-menu-${bill.id}`}
      >
        <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute right-0 top-full mt-1 w-[160px] z-[91] rounded-lg overflow-hidden"
            style={{ background: 'var(--ara-surface)', border: '1px solid var(--ara-line)' }}
          >
            <button
              type="button"
              onClick={() => { setOpen(false); onPaid?.(); }}
              className={`w-full text-left px-3 py-2 text-[13px] hover:opacity-70 transition-ara ${isPaid ? 'opacity-40 pointer-events-none' : ''}`}
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid={`bill-mark-paid-${bill.id}`}
            >
              Mark paid
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onSchedule?.(); }}
              className="w-full text-left px-3 py-2 text-[13px] hover:opacity-70 transition-ara"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid={`bill-schedule-${bill.id}`}
            >
              Schedule
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onReview?.(); }}
              className="w-full text-left px-3 py-2 text-[13px] hover:opacity-70 transition-ara"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid={`bill-review-${bill.id}`}
            >
              Review
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* Keep Figure import referenced */
void Figure;
